/**
 * Panel de posibles duplicados (§6.4).
 *
 * Cada par se resuelve con un clic: fusionar —eligiendo cuál se conserva— o
 * marcarlos como distintos. La decisión es persistente: el backend no vuelve a
 * proponer ese par, ni siquiera tras nuevas corridas.
 *
 * Se muestran los dos registros lado a lado con lo que los distingue, porque
 * decidir sin ver la categoría, el tipo y el número de fuentes es adivinar.
 */

import { ArrowLeftRight, Check, GitMerge, SplitSquareHorizontal } from 'lucide-react'
import { useState } from 'react'

import { useDecidirDuplicado, useDuplicados } from '../../api/hooks'
import type { Duplicado, StakeholderFila } from '../../api/tipos'
import { mensajeDeError, useAvisos } from '../../ui/avisos-contexto'
import {
  Boton,
  Chip,
  EscaleraNivel,
  EsqueletoTabla,
  EtiquetaPostura,
  EtiquetaTipo,
  Error as EstadoError,
  Vacio,
} from '../../ui/primitivos'
import { cx } from '../../ui/util'

function Tarjeta({
  fila,
  elegido,
  onElegir,
  onAbrir,
}: {
  fila: StakeholderFila
  elegido: boolean
  onElegir: () => void
  onAbrir: () => void
}) {
  return (
    <div
      className={cx(
        'rounded-md border p-2.5 transition-colors',
        elegido
          ? 'border-[var(--color-acento)] bg-[var(--color-acento-suave)]'
          : 'border-[var(--color-borde)] bg-[var(--color-panel)]',
      )}
    >
      <div className="flex items-start gap-2">
        <input
          type="radio"
          checked={elegido}
          onChange={onElegir}
          className="mt-1 shrink-0 accent-[var(--color-acento)]"
          aria-label={`Conservar ${fila.nombre}`}
        />
        <div className="min-w-0 flex-1">
          <button
            onClick={onAbrir}
            className="block max-w-full truncate text-left text-[13px] font-medium hover:text-[var(--color-acento)] hover:underline"
          >
            {fila.nombre}
            {fila.sigla && (
              <span className="ml-1 text-[11px] text-[var(--color-texto-tenue)]">
                ({fila.sigla})
              </span>
            )}
          </button>
          <p className="mt-0.5 truncate text-[11px] text-[var(--color-texto-tenue)]">
            {fila.categoria || 'Sin categoría'}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
            <EtiquetaTipo tipo={fila.tipo} />
            <EtiquetaPostura postura={fila.postura} />
            <span className="inline-flex items-center gap-1 text-[var(--color-texto-suave)]">
              <EscaleraNivel nivel={fila.nivel_influencia} titulo="Influencia" />
              influencia
            </span>
            <span className="tabular text-[var(--color-texto-suave)]">
              {fila.n_fuentes} fuentes
            </span>
            {fila.corroborado && <Chip>corroborado</Chip>}
          </div>
        </div>
      </div>
    </div>
  )
}

function Par({
  duplicado,
  entidadId,
  onAbrir,
}: {
  duplicado: Duplicado
  entidadId: number
  onAbrir: (id: number) => void
}) {
  const avisos = useAvisos()
  const decidir = useDecidirDuplicado(entidadId)
  // Por defecto se conserva el que tiene más fuentes: suele ser la ficha más
  // completa, y si empatan queda el izquierdo, que es el más antiguo.
  const [destinoId, setDestinoId] = useState(
    duplicado.derecho_detalle.n_fuentes > duplicado.izquierdo_detalle.n_fuentes
      ? duplicado.derecho
      : duplicado.izquierdo,
  )

  const nombreDestino =
    destinoId === duplicado.izquierdo
      ? duplicado.izquierdo_detalle.nombre
      : duplicado.derecho_detalle.nombre

  function resolver(decision: 'fusionar' | 'distintos') {
    decidir.mutate(
      {
        id: duplicado.id,
        decision,
        destinoId: decision === 'fusionar' ? destinoId : undefined,
      },
      {
        onSuccess: () =>
          avisos.exito(
            decision === 'fusionar'
              ? `Fusionados en «${nombreDestino}»`
              : 'Marcados como actores distintos',
            {
              detalle:
                decision === 'distintos'
                  ? 'No se volverá a proponer este par, ni siquiera tras nuevas corridas.'
                  : undefined,
            },
          ),
        onError: (error) => avisos.error('No se pudo resolver el par', mensajeDeError(error)),
      },
    )
  }

  return (
    <li className="rounded-lg border border-[var(--color-borde)] bg-[var(--color-lienzo)] p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[12px] text-[var(--color-texto-suave)]">{duplicado.motivo}</span>
        <Chip titulo="Similitud entre los nombres">{duplicado.score.toFixed(0)}% parecido</Chip>
      </div>

      <div className="grid items-stretch gap-2 sm:grid-cols-[1fr_auto_1fr]">
        <Tarjeta
          fila={duplicado.izquierdo_detalle}
          elegido={destinoId === duplicado.izquierdo}
          onElegir={() => setDestinoId(duplicado.izquierdo)}
          onAbrir={() => onAbrir(duplicado.izquierdo)}
        />
        <div className="flex items-center justify-center text-[var(--color-texto-tenue)]">
          <ArrowLeftRight size={15} />
        </div>
        <Tarjeta
          fila={duplicado.derecho_detalle}
          elegido={destinoId === duplicado.derecho}
          onElegir={() => setDestinoId(duplicado.derecho)}
          onAbrir={() => onAbrir(duplicado.derecho)}
        />
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <Boton
          variante="primario"
          icono={<GitMerge size={13} />}
          cargando={decidir.isPending && decidir.variables?.decision === 'fusionar'}
          onClick={() => resolver('fusionar')}
        >
          Son el mismo · conservar «{nombreDestino}»
        </Boton>
        <Boton
          variante="secundario"
          icono={<SplitSquareHorizontal size={13} />}
          cargando={decidir.isPending && decidir.variables?.decision === 'distintos'}
          onClick={() => resolver('distintos')}
        >
          Son distintos
        </Boton>
        <span className="text-[11px] text-[var(--color-texto-tenue)]">
          Marca cuál conservar en el radio de cada tarjeta.
        </span>
      </div>
    </li>
  )
}

export function PanelDuplicados({
  entidadId,
  onAbrir,
}: {
  entidadId: number
  onAbrir: (id: number) => void
}) {
  const consulta = useDuplicados(entidadId)

  if (consulta.isLoading) return <EsqueletoTabla filas={3} columnas={3} />
  if (consulta.isError) {
    return <EstadoError error={consulta.error} reintentar={() => consulta.refetch()} />
  }

  const pares = consulta.data ?? []
  if (!pares.length) {
    return (
      <Vacio
        titulo="No hay duplicados por revisar"
        detalle="Los pares con evidencia de ser el mismo actor se fusionaron solos al ingerir; los que se distinguen con claridad ni se proponen. Aquí solo llega lo genuinamente dudoso."
        icono={<Check size={22} />}
      />
    )
  }

  return (
    <div className="p-3">
      <p className="mb-3 text-[12px] text-[var(--color-texto-suave)]">
        {pares.length} par{pares.length === 1 ? '' : 'es'} por decidir. Cada decisión es
        definitiva: un par marcado como distinto no se vuelve a proponer.
      </p>
      <ul className="grid gap-3">
        {pares.map((d) => (
          <Par key={d.id} duplicado={d} entidadId={entidadId} onAbrir={onAbrir} />
        ))}
      </ul>
    </div>
  )
}
